import React, { useState, useEffect, useCallback } from "react";
import { setTitle } from "../helpers/MetaTag";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { reorder } from "../helpers";
import toastr from "toastr";
import moment from "moment";
import { useSelector } from "react-redux";
import { postData } from "../services/api";
import Pagination from "../components/Common/Pagination";
import Button from "../components/form/Button";
import { Tooltip } from "react-tooltip";
import { useTranslation } from "react-i18next";
import ManagePositionGroupPopup from "../components/popup/ManagePositionGroupPopup";
import ManageCompanyPositionGroup from "../components/popup/ManageCompanyPositionGroup";
import AssignPositionTierPopup from "../components/popup/AssignPositionTierPopup";

// ─── Confirm Delete Dialog ────────────────────────────────────────────────────
const ConfirmDialog = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4">
        <div className="px-6 py-6 text-center">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fa-solid fa-triangle-exclamation text-red-500 text-2xl" />
          </div>
          <p className="text-slate-700 font-medium mb-6">
            Are you sure you want to delete this tier? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-lg border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-5 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Card Skeleton ────────────────────────────────────────────────────────────
const CardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 animate-pulse">
    <div className="flex justify-between mb-4">
      <div className="h-4 bg-slate-200 rounded w-2/3" />
      <div className="h-4 bg-slate-100 rounded w-12" />
    </div>
    <div className="space-y-2 mb-4">
      <div className="h-3 bg-slate-100 rounded w-1/2" />
      <div className="h-3 bg-slate-100 rounded w-2/3" />
      <div className="h-3 bg-slate-100 rounded w-1/3" />
    </div>
    <div className="h-7 bg-slate-100 rounded-full w-20" />
  </div>
);

// ─── Accent palette cycling per card ─────────────────────────────────────────
const ACCENTS = [
  { top: "border-t-teal-400",   badge: "bg-teal-500",   soft: "bg-teal-50 text-teal-700 border-teal-200",   icon: "text-teal-500"   },
  { top: "border-t-blue-400",   badge: "bg-blue-500",   soft: "bg-blue-50 text-blue-700 border-blue-200",   icon: "text-blue-500"   },
  { top: "border-t-violet-400", badge: "bg-violet-500", soft: "bg-violet-50 text-violet-700 border-violet-200", icon: "text-violet-500" },
  { top: "border-t-amber-400",  badge: "bg-amber-500",  soft: "bg-amber-50 text-amber-700 border-amber-200",  icon: "text-amber-500"  },
  { top: "border-t-rose-400",   badge: "bg-rose-500",   soft: "bg-rose-50 text-rose-700 border-rose-200",   icon: "text-rose-500"   },
];

// ─── Single Tier Card ─────────────────────────────────────────────────────────
const TierCard = ({ value, index, accent, t, onEdit, onDelete, onStatusChange, onAssign, provided }) => {
  const isActive = value?.status === 1;

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={`bg-white rounded-2xl border-t-4 ${accent.top} border border-slate-100 shadow-sm hover:shadow-md transition-all flex flex-col group`}
    >
      {/* Drag handle + header */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          {/* Drag handle */}
          <div
            {...provided.dragHandleProps}
            className="mt-1 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing flex-shrink-0"
            title="Drag to reorder"
          >
            <i className="fa-regular fa-grip-dots-vertical text-base" />
          </div>

          {/* Name */}
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-slate-800 text-base leading-snug truncate">
              {value?.name}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {value?.created_at
                ? moment(new Date(value.created_at)).format("MMM Do YYYY")
                : "N/A"}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onEdit(value._id)}
              title="Edit"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <i className="fa-regular fa-pen-to-square text-sm" />
            </button>
            <button
              onClick={() => onAssign(value._id)}
              title="Assign Position"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-teal-50 hover:text-teal-600 transition-colors"
            >
              <i className="fa-solid fa-layer-group text-sm" />
            </button>
            <button
              onClick={() => onDelete(value._id)}
              title="Delete"
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
            >
              <i className="fa-regular fa-trash text-sm" />
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 mx-5" />

      {/* Stats row */}
      <div className="px-5 py-3 flex flex-col gap-2 flex-1">
        {/* Questions */}
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${accent.soft} border flex-shrink-0`}>
            <i className="fa-regular fa-circle-question text-xs" />
          </div>
          <div>
            <p className="text-[11px] text-slate-400 leading-none">Questions</p>
            <p className="text-sm font-bold text-slate-700 leading-snug">
              {value?.company_heading_details?.length || 0}
            </p>
          </div>
        </div>

        {/* Positions */}
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${accent.soft} border flex-shrink-0`}>
            <i className="fa-regular fa-briefcase text-xs" />
          </div>
          <div>
            <p className="text-[11px] text-slate-400 leading-none">Positions</p>
            <p className="text-sm font-bold text-slate-700 leading-snug">
              {value?.total_positions || 0}{" "}
              <span className="font-normal text-slate-500 text-xs">
                {t("positions_assign_in_the_tier")}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 mx-5" />

      {/* Footer: status toggle */}
      <div className="px-5 py-3 flex items-center justify-between">
        <button
          onClick={() => onStatusChange(value)}
          title="Click to toggle status"
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full py-1 px-3 border transition-all ${
            isActive
              ? "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100"
              : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-400"}`} />
          {isActive ? t("active") : t("deactivated")}
        </button>

        {/* <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-[11px] font-bold ${accent.badge}`}>
          {index + 1}
        </span> */}
      </div>
    </div>
  );
};

const CompanyPositionGroup = () => {
  setTitle("Succession Planning Software for your Business with Succession Now | Position Group");

  const { t } = useTranslation();
  const user = useSelector((state) => state.auth?.user);
  const isTrial = localStorage.getItem("isTrial");

  const [loading, setLoading] = useState(true);
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [results, setResults] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [startDataCount, setStartDataCount] = useState(0);
  const [endDataCount, setEndDataCount] = useState(0);
  const [noOfPage, setNoOfPage] = useState(0);
  const [isUpdate, setIsUpdate] = useState(false);

  const [isOpen, setIsopen] = useState({ _id: null, open: false });
  const [assignPosition, setAssignPosition] = useState({ _id: null, open: false });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  // ── Load list ──────────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await postData("filter-item/list", {
        sortQuery: { created_at: -1 },
        limit,
        offset,
        type: "position-group",
      });
      if (result.status === 200) {
        setTotalDataCount(result.count);
        setResults(result.data);
      }
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  }, [limit, offset, isUpdate]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    setNoOfPage(Math.ceil(totalDataCount / limit));
    const startItem = Number(totalDataCount > 0 ? 1 : 0);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem);
  }, [limit, totalDataCount]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const handlePageClick = (event) => {
    const newOffset = (event.selected * limit) % totalDataCount;
    setOffset(newOffset);
    const startItem = Number(newOffset + 1);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem);
  };

  // ── Drag & drop reorder ────────────────────────────────────────────────────
  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;
    const items = reorder(results, result.source.index, result.destination.index);
    setResults(items);
    const res = await postData("position-group/ordering", { items });
    if (res.status === 200) {
      toastr.success(res.message);
    } else {
      toastr.error(res.message);
    }
  };

  // ── Status change ──────────────────────────────────────────────────────────
  const handleStatusChange = async (value) => {
    const findData = results.find((item) => item._id === value._id);
    if (findData?.total_questions > 0) {
      toastr.warning("You cannot deactivate because there are questions assigned under this position group.");
      return;
    }
    setIsUpdate(false);
    try {
      const newStatus = value.status === 1 ? 2 : 1;
      const statusData = await postData("filter-item/status-change", {
        id: value._id,
        status: newStatus,
      });
      if (statusData.data) {
        setResults(results.map((it) =>
          it._id === statusData.data._id ? { ...it, status: statusData.data.status } : it
        ));
        setIsUpdate(true);
      } else {
        toastr.error(statusData.message);
      }
    } catch (error) {
      toastr.error(error.message);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    setIsUpdate(false);
    try {
      const statusData = await postData("filter-item/delete", { id: deleteDialog.id });
      if (statusData.data) {
        setResults((prev) => prev.filter((item) => item._id !== deleteDialog.id));
        setDeleteDialog({ open: false, id: null });
        setIsUpdate(true);
      } else {
        toastr.error(statusData.message);
      }
    } catch (error) {
      toastr.error(error.message);
    }
  };

  return (
    <>
      <div className="border bg-white border-gray-200 p-4 shadow-md rounded-xl">
        {/* Header */}
        <div className="relative flex gap-3 items-center mb-6 flex-wrap sm:flex-nowrap">
          <div className="text-2xl font-bold">{t("tiers")}</div>
          {!isTrial && (
            <div
              className="sm:ml-auto flex justify-end items-center"
              data-tooltip-id="buttonPositionGroup1"
              data-tooltip-place="right"
            >
              <Button
                buttonType="button"
                buttonIcon="fa-light fa-plus"
                buttonIconPosition="left"
                buttonLabel={t("add")}
                buttonHasLink={false}
                functions={() => setIsopen({ _id: null, open: true })}
              />
              <Tooltip
                id="buttonPositionGroup1"
                className="!text-xs !bg-teal-500 z-50 rounded py-2 px-2 !opacity-100"
                render={() => (
                  <div className="text-[13px] font-semibold">{t("create_new_group")}</div>
                )}
              />
            </div>
          )}
        </div>

        {/* Cards Grid */}
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="tier-cards" direction="horizontal">
            {(droppableProvided) => (
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5"
              >
                {/* Loading skeletons */}
                {loading &&
                  [1, 2, 3].map((i) => <CardSkeleton key={i} />)}

                {/* Empty state */}
                {!loading && results.length === 0 && (
                  <div className="col-span-4 text-center py-20">
                    <i className="fa-light fa-layer-group text-slate-300 text-5xl mb-4 block" />
                    <p className="text-slate-500 font-medium">
                      No tiers found. Click <strong>Add</strong> to create one.
                    </p>
                  </div>
                )}

                {/* Cards */}
                {!loading &&
                  results.map((value, index) => (
                    <Draggable
                      key={value._id}
                      draggableId={value._id.toString()}
                      index={index}
                    >
                      {(provided) => (
                        <TierCard
                          key={value._id}
                          value={value}
                          index={index}
                          accent={ACCENTS[index % ACCENTS.length]}
                          t={t}
                          provided={provided}
                          onEdit={(id) => setIsopen({ open: true, _id: id })}
                          onDelete={(id) => setDeleteDialog({ open: true, id })}
                          onStatusChange={handleStatusChange}
                          onAssign={(id) => setAssignPosition({ open: true, _id: id })}
                        />
                      )}
                    </Draggable>
                  ))}

                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* Pagination */}
        <div className="mt-6">
          {noOfPage > 1 ? (
            <Pagination
              handlePageClick={handlePageClick}
              noOfPage={noOfPage}
              startDataCount={startDataCount}
              endDataCount={endDataCount}
              count={totalDataCount}
            />
          ) : (
            totalDataCount > 0 && (
              <div className="flex justify-center sm:justify-between my-2 text-sm text-black text-center sm:text-left">
                <p>
                  {t("showing")}: {startDataCount} to {endDataCount} of {totalDataCount}
                </p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Modals */}
      {isOpen?.open && (
        <ManageCompanyPositionGroup
          isOpen={isOpen?.open}
          id={isOpen?._id}
          setIsOpen={setIsopen}
          loadList={loadList}
        />
      )}
      {assignPosition?.open && (
        <AssignPositionTierPopup
          isOpen={assignPosition?.open}
          id={assignPosition?._id}
          setIsOpen={setAssignPosition}
          loadList={loadList}
        />
      )}

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default CompanyPositionGroup;